import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { ChatLauncherSpacer } from "@/components/layout/ChatLauncherSpacer";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileText, Link as LinkIcon, MoreVertical, Trash2, Upload } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Document } from "@/types/documents";
import type { TreeFolder } from "../types";
import type { BuildingProject } from "@/types/projects";

// Import existing components
import { DocumentsEmptyState } from "./DocumentsEmptyState";
import { NewDocumentFlow } from "./templateForm/NewDocumentFlow";
import { STORAGE_ADMISSION_EVENT } from "@/domains/documents/utils/storageBatchFeedback";
import { UploadModal, type UploadDestination } from "./UploadModal";
import { DocumentViewerDialog } from "./DocumentViewerDialog";
import { ShareDialog } from "./organisms/ShareDialog";

import { DocumentListTable } from "./organisms";
import { ServerDocumentTimeline } from "./organisms/DocumentTimeline";
import { DocumentSearchStatusNotice } from "./molecules/DocumentSearchStatusNotice";
import { SignaturePanelProvider } from "./organisms/SignaturePanelProvider";
import { useDocumentCommentsPanel } from "../comments/documentCommentsPanelContext";
import { UploadCollisionDialog } from "./organisms/UploadCollisionDialog";
import { BulkActionBar } from "./molecules/BulkActionBar";
import { IfcDiffOverviewDialog } from "@/domains/buildings/components/ifc-viewer/diff/IfcDiffOverviewDialog";
import { useIfcDiffOverview, type IfcDiffCandidateDoc } from "../hooks/useIfcDiffOverview";
import { useDrawingViewer } from "../hooks/useDrawingViewer";
import { useDocumentVerification, verifiedSelectionState } from "../hooks/useDocumentVerification";
import { SignDocumentsDialog } from "./organisms/SignDocumentsDialog";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";

// Import hooks
import { useProjects } from "../hooks/useProjects";
import { useDocumentFilters } from "../context/DocumentFiltersContext";
import { useTagFiltering } from "../hooks/useTagFiltering";
import { useExternalFileDrop } from "../hooks/useExternalFileDrop";
import { folderService } from "../services/folderService";
import { parseTagFilters } from "../utils/parseTagFilters";
import { withoutLockedProjectFilters } from "./filters/documentFilterRowUtils";
import { DocumentUpload } from "./DocumentUpload";
import { PermissionGuard } from "@/components/permissions/PermissionGuard";
import { PageHeaderActions } from "@/components/layout/PageHeaderActions";
import { CreateGlyph, HeaderActionIconButton } from "@/components/layout/HeaderActionIconButton";
import { PERMISSIONS } from "@/constants/permissions";
import { useDocumentUpload } from "@/hooks/useDocumentUpload";
import { UploadQueueProgress } from "@/components/documents/UploadQueueProgress";
import { toast } from "sonner";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useProjectActionGuard } from "@/hooks/usePermissionDenied";
import { useSigningAccess } from "@/domains/signing/useSigningAccess";
import type { StagedVersionItem } from "@/domains/documents/types/addVersions";
import type { UploadSelection } from "../utils/uploadSelection";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

interface DocumentsMainViewProps {
  buildingId: string;
  buildingName: string;
  documents: Document[];
  folders: TreeFolder[];
  isLoading: boolean;
  searchQuery: string;
  chatSearchResults: { query: string; documents: any[] } | null;
  onDownloadDocument: (document: Document) => void;
  onDeleteDocument: (document: Document) => void;
  /** Submit handler for document rename. Receives the doc id (as a string) and
   *  the new full filename (basename + preserved extension). The repository
   *  layer validates and surfaces a typed error; the parent shows the toast. */
  onRenameDocument: (docId: string, newFilename: string) => Promise<void> | void;
  /** Predicate to gate the per-row Rename action visibility. */
  canRenameDocument?: (doc: { project_id?: number | string | null }) => boolean;
  onCategorizeDocument?: (document: Document) => void;
  onMoveDocuments?: (documents: Document[], targetFolder: string) => void;
  onFileDrop: (files: File[]) => void;
  onClearSearchResults: () => void;
  onRefreshDocuments: () => Promise<void>;
  onViewTrash: () => void;
  onUpdateDocumentTags?: (documentId: string, folderTags: string[]) => void;
  selectedProjectId?: string | null;
  currentPage?: number;
  totalCount?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onAskFranklin?: (docs: Array<{ id: string; filename: string }>) => void;
  viewMode: "list" | "timeline";
  /** Opens the Manage Shares dialog (owned by the page, which renders it). */
  onManageShares: () => void;
}

export const DocumentsMainView = observer(function DocumentsMainView({
  buildingId,
  buildingName,
  documents,
  folders,
  isLoading,
  searchQuery,
  chatSearchResults,
  onDownloadDocument,
  onDeleteDocument,
  onRenameDocument,
  canRenameDocument,
  onCategorizeDocument,
  onMoveDocuments,
  onFileDrop,
  onClearSearchResults,
  onRefreshDocuments,
  onViewTrash,
  onUpdateDocumentTags,
  selectedProjectId,
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
  onAskFranklin,
  viewMode,
  onManageShares,
}: DocumentsMainViewProps) {
  const { t } = useLanguage();
  const { filters, reportResolvedSearchQuery } = useDocumentFilters();
  // Read the same set the query does. A locked project scope owns the project
  // dimension outright — the panel strips `project-*` ids from what it writes
  // and `storeFilters` overwrites them with the scope — so ids left over from
  // before the scope was locked narrow nothing. Counting them here claimed
  // filters were active, which hid the empty state and put an "X of Y" ratio on
  // an unfiltered list, with a Clear-all that changed no rows.
  // The Added date counts too: leaving it out showed the "Upload your first
  // documents" onboarding to a building whose files were merely older than it.
  const hasActiveFilters =
    withoutLockedProjectFilters(filters.activeTagFilters, !!selectedProjectId).length > 0 ||
    filters.searchQuery.length > 0 ||
    !!filters.addedDate;
  const { canVerifyDocument, setVerified } = useDocumentVerification();
  const { projects } = useProjects(buildingId);
  const documentStore = useDocumentStore();

  // Authentication and organization context
  const { userProfile } = useAuth();
  const { currentOrganization, isReady } = useOrganizationContext();
  // `hasPermission` / `isPlatformAdmin` are no longer read here: the signing
  // gate below moved into `useSigningAccess()` verbatim. Drawing Compare's
  // gate is develop's — it replaced the old `canUseIfcDiff` read.
  const { canViewDrawingCompareTool, canWriteFilesInProject } = usePermissions();

  // Digital signing — platform admins always; otherwise the org rollout flag
  // AND the ALLOW_SIGNING role permission (see useSigningAccess). When neither
  // holds, the Sign button is hidden from the bulk bar across both the list
  // and timeline views.
  const signingEnabled = useSigningAccess();
  const [isSignDialogOpen, setIsSignDialogOpen] = useState(false);
  // One specific container to sign, asked for by the signature panel. Kept
  // separate from the selection because a timeline row lives outside the list's
  // `documents` array, so resolving it through the selection would sign nothing.
  const [signTarget, setSignTarget] = useState<{
    id: number;
    filename: string;
    size: number;
  } | null>(null);
  const [selectedDocumentsForSigning, setSelectedDocumentsForSigning] = useState<
    Array<{ id: number; filename: string; size: number }>
  >([]);
  // Resolve the multi-selection to signable documents through the store's
  // single resolver, which unions every selectable surface — the paged/project
  // `documents` prop AND the timeline's own rows (individual docs + expanded
  // clusters) — and recovers ids no surface currently renders, exactly like
  // bulk download and bulk trash. Filtering only the `documents` prop is why
  // the timeline Sign dialog resolved 0 docs; resolving without the recovery is
  // why zooming out (which drops the timeline row cache) would silently shrink
  // the set sent for signature. Resolved on open, not in render, because the
  // recovery is a round-trip.
  //
  // The resolve is a round-trip, so the selection can move under it: unchecking
  // a row (or a second click landing out of order) while it is in flight would
  // otherwise open the dialog on the pre-change set and submit THAT for
  // signature. So: one resolve at a time, and the result is only used if the
  // selection it was computed from is still the current one.
  const isResolvingSignature = useRef(false);
  const openSignDialog = useCallback(async () => {
    if (isResolvingSignature.current) return;
    const selectionAtRequest = documentStore.selectedDocumentIds.join(",");
    isResolvingSignature.current = true;
    try {
      const { docs, unresolvedCount } =
        await documentStore.resolveSelectionForBulkAction(documents);
      if (documentStore.selectedDocumentIds.join(",") !== selectionAtRequest) {
        toast.warning("The selection changed while it was being checked — press Sign again.");
        return;
      }
      if (unresolvedCount > 0) {
        toast.warning(`${unresolvedCount} selected document(s) could not be found`);
      }
      // Nothing resolved: the dialog would open empty with its submit button
      // permanently disabled and no clue why. The warning above is the reason;
      // if there wasn't one, say so here.
      if (docs.length === 0) {
        if (unresolvedCount === 0) toast.warning("No documents available to sign");
        return;
      }
      setSelectedDocumentsForSigning(
        docs.map((d) => ({ id: Number(d.id), filename: d.filename, size: d.size ?? 0 }))
      );
      setIsSignDialogOpen(true);
    } catch (error) {
      // Never open the dialog on an unverified selection — signing the wrong
      // set of documents is not recoverable.
      console.error("Failed to resolve the selection for signing:", error);
      toast.error(t.common.error);
    } finally {
      isResolvingSignature.current = false;
    }
  }, [documentStore, documents, t]);

  // Track whether the main content has rendered at least once.
  // Once it has, never replace it with a full-page spinner (which would unmount
  // the document table and its search input, losing focus).
  const hasRenderedContentRef = useRef(false);

  // Convert UI tag filters to timeline filter params for server-side filtering.
  // The global project scope always wins on the project dimension, so the
  // timeline (buckets, counts, individual docs, content search) never shows
  // other projects — even when no tag chips / search are active.
  const timelineFilters = useMemo(() => {
    const scopeProjectIds = selectedProjectId ? [Number(selectedProjectId)] : undefined;
    const hasUserFilters =
      filters.activeTagFilters.length > 0 || !!filters.searchQuery || !!filters.addedDate;
    if (!hasUserFilters && !scopeProjectIds) {
      return undefined;
    }

    const parsed = parseTagFilters(filters.activeTagFilters, documentStore.availableFilters);

    return {
      projectIds: scopeProjectIds ?? parsed.projectIds,
      categoryIds: parsed.categoryIds,
      designDisciplineIds: parsed.designDisciplineIds,
      designStageIds: parsed.designStageIds,
      searchText: filters.searchQuery || undefined,
      verifiedOnly: parsed.verifiedOnly,
      uploadDateRange: filters.addedDate?.range,
    };
  }, [
    filters.activeTagFilters,
    filters.searchQuery,
    filters.addedDate,
    documentStore.availableFilters,
    selectedProjectId,
  ]);

  // The Timeline owns the fetch on its tab, so it owns the search box's busy
  // state there. Reported from the query that was committed when the fetch was
  // issued — `timelineFilters` is memoised on it, so it is the one the settled
  // fetch belongs to whatever has been typed since.
  const timelineSearchQuery = filters.searchQuery;
  const reportTimelineResolved = useCallback(() => {
    reportResolvedSearchQuery(timelineSearchQuery);
  }, [reportResolvedSearchQuery, timelineSearchQuery]);

  // Leaving the Timeline hands the box to the List tab, which reports its own
  // load. Without this the box would keep showing the Timeline's last verdict
  // while the List was still fetching.
  const isTimelineView = viewMode === "timeline";
  useEffect(() => {
    if (!isTimelineView) return;
    return () => reportResolvedSearchQuery(null);
  }, [isTimelineView, reportResolvedSearchQuery]);

  const [selectedProject, setSelectedProject] = useState<BuildingProject | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<{ buildingId: string; orgId: string }>).detail;
      if (detail.buildingId === buildingId && detail.orgId === currentOrganization?.id)
        setIsUploadModalOpen(true);
    };
    window.addEventListener(STORAGE_ADMISSION_EVENT, listener);
    return () => window.removeEventListener(STORAGE_ADMISSION_EVENT, listener);
  }, [buildingId, currentOrganization?.id]);
  const [isNewDocumentModalOpen, setIsNewDocumentModalOpen] = useState(false);
  const newDocumentButtonRef = useRef<HTMLButtonElement>(null);
  // Null unless the building is scoped to a project, and deliberately so: at
  // building scope there is no right project to assume, so Save asks for one
  // and the attachment picker offers the choice. An earlier fix guessed at the
  // building's newest project, which bound the form to an arbitrary one without
  // saying so. (It was reaching for `is_default` first — a column nothing in
  // the app ever sets, false on every project of every building.)
  const templateFormProjectId = selectedProjectId ?? null;
  const [pendingSelection, setPendingSelection] = useState<UploadSelection | null>(null);
  const [viewerDocument, setViewerDocument] = useState<Document | null>(null);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [hadRecentUpload, setHadRecentUpload] = useState(false);

  // Share dialog state
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  // Use the document upload hook for actual file upload functionality
  // NOTE: We set requireProject to false and handle validation ourselves
  // This is because the hook captures projectId at creation time and doesn't update dynamically
  const {
    handleBulkUpload,
    isUploading,
    fileProgresses,
    uploadQueueStore,
    addVersionsInOrder,
    collisionDialog,
  } = useDocumentUpload({
    buildingId,
    // This view only uploads through its drop zone.
    analyticsSource: "drag",
    // Trigger refresh when uploads complete to update document list
    onUploadComplete: () => {
      onRefreshDocuments();
    },
    onDataExtracted: (data) => {
      console.log("Data extracted from uploaded document:", data);
    },
    initialCategory: "documents",
    projectId: selectedProject?.id?.toString(),
    requireProject: false, // We'll validate project manually
  });

  /**
   * Adapt DocumentWithTags row → upload hook's target shape, using the row's
   * own `project_id` when set (rows aren't always under `selectedProject`).
   */
  const handleAddNewVersions = useCallback(
    async (
      items: StagedVersionItem[],
      doc: {
        id: string;
        filename: string;
        version_group: string | null;
        project_id?: number | null;
        folder_id?: string | null;
        parent_document_id: string | null;
        container_type?: string | null;
      }
    ) => {
      return await addVersionsInOrder(
        items,
        {
          id: doc.id,
          filename: doc.filename,
          version_group: doc.version_group,
          project_id: doc.project_id ?? null,
          folder_id: doc.folder_id ? parseInt(doc.folder_id, 10) : null,
          parent_document_id: doc.parent_document_id,
          container_type: doc.container_type ?? null,
        },
        doc.project_id !== null && doc.project_id !== undefined ? String(doc.project_id) : undefined
      );
    },
    [addVersionsInOrder]
  );

  // Handler for click on drop zone - open modal without pending files
  // Every upload and filed form lands in a project. Scoped to one project,
  // that project's permission decides; in building scope, at least one
  // project the member may add to (the dialogs then offer only those). A
  // member who may not is told so, and who can grant it, on the press.
  const scopedProjectId = selectedProjectId ? Number(selectedProjectId) : null;
  const scopeProjectIds = useMemo(() => projects.map((project) => Number(project.id)), [projects]);
  const guardAddFiles = useProjectActionGuard("addFiles", canWriteFilesInProject, scopeProjectIds);

  const handleUploadButtonClick = () =>
    guardAddFiles(scopedProjectId, () => {
      setPendingSelection(null);
      setIsUploadModalOpen(true);
    });

  // Clear any document selection when leaving the documents view, so a
  // selection made here does not persist into other pages (e.g. Projects).
  useEffect(() => {
    return () => documentStore.clearSelection();
  }, [documentStore]);

  // Handler for drag and drop - open modal with pending files
  const handleDragAndDrop = (selection: UploadSelection) =>
    guardAddFiles(scopedProjectId, () => {
      setPendingSelection(selection);
      setIsUploadModalOpen(true);
    });

  // Page-level external-file drop. Neither List nor Timeline is scoped to a
  // folder, so a dropped batch opens the upload dialog to confirm where it goes.
  const { isDragOver, dropZoneProps } = useExternalFileDrop({
    onFiles: handleDragAndDrop,
  });

  const closeUploadModal = () => {
    setIsUploadModalOpen(false);
    setPendingSelection(null);
  };

  // The project the page is scoped to, if any. The upload dialog then offers
  // no project choice and starts at that project's root.
  const scopedUploadProject = selectedProjectId
    ? (projects.find((p) => p.id === selectedProjectId) ?? null)
    : null;

  const handleUpload = async (selection: UploadSelection, destination: UploadDestination) => {
    const project = projects.find((p) => p.id === destination.projectId);
    if (project) setSelectedProject(project);
    closeUploadModal();

    // All uploads go through the queue system. The selection's folder tree is
    // created under the destination before any file is queued.
    await handleBulkUpload(
      selection.files,
      selection.relativePaths,
      destination.projectId,
      destination.folderId ?? undefined,
      selection.directoryPaths
    );
  };

  // Auto-select project from URL parameter
  useEffect(() => {
    if (selectedProjectId && projects.length > 0) {
      const projectFromUrl = projects.find((p) => p.id === selectedProjectId);
      if (projectFromUrl && selectedProject?.id !== selectedProjectId) {
        setSelectedProject(projectFromUrl);
      }
    }
  }, [selectedProjectId, projects, selectedProject?.id]);

  // Manage upload progress visibility
  useEffect(() => {
    // Only count files that are actively being processed (not complete or failed)
    const activeFiles = fileProgresses.filter(
      (f) => f.status === "uploading" || f.status === "processing"
    );

    // Track if we had files recently
    if (fileProgresses.length > 0 || isUploading) {
      setHadRecentUpload(true);
    }

    const shouldShow = isUploading || activeFiles.length > 0;

    if (shouldShow) {
      setShowUploadProgress(true);
    } else {
      // Hide immediately when no active uploads
      setShowUploadProgress(false);
      setHadRecentUpload(false);
    }
  }, [isUploading, fileProgresses, hadRecentUpload]);

  // Tag filtering is now handled server-side in BuildingDocuments via loadDocuments.
  // Documents passed to this component are already filtered by the store.
  // The useTagFiltering hook is kept for backwards compatibility but effectively no-ops
  // since the documents are pre-filtered.
  const { filteredDocuments: tagFilteredDocumentsRaw } = useTagFiltering({
    documents: documents || [],
    activeFilters: [], // Pass empty array - server handles filtering now
  });

  // WHA-3695: the timeline view offers the same "Compare IFCs" bulk action as
  // the list view. Resolve candidates through the store's single resolver so
  // the timeline's own rows (individual docs + expanded clusters) are included
  // alongside the paged `documents` slice — the same union Sign/download use,
  // instead of re-deriving it here. Declared above the org-loading early
  // return: hooks must run on every render.
  const ifcDiffCandidates: IfcDiffCandidateDoc[] = documentStore.allSelectableDocuments(
    documents ?? []
  );
  const timelineIfcDiff = useIfcDiffOverview(ifcDiffCandidates, (id) =>
    documentStore.isSelected(String(id))
  );

  // Same story for the DWG View/Compare actions: the timeline's bulk bar is a
  // separate instance from the list view's, so it needs its own wiring off the
  // same candidate set and the same store selection.
  const timelineDrawings = useDrawingViewer(ifcDiffCandidates, documentStore.selectedDocumentIds, {
    enabled: canViewDrawingCompareTool,
    buildingId,
  });

  // Verified mark for the timeline's bulk bar. The selection is resolved by the
  // store, since timeline rows never land in its `documents` map. Offered only
  // when every selected row is writable — a partially-permitted bulk action
  // would half-succeed and leave the rest to be rejected by RLS.
  const selectedForVerify = documentStore.selectedVerifiableDocuments;
  const timelineVerifiedState = verifiedSelectionState(selectedForVerify);
  const canVerifySelection =
    selectedForVerify.length > 0 && selectedForVerify.every((doc) => canVerifyDocument(doc));
  const commentsPanel = useDocumentCommentsPanel();
  const timelineVerify = {
    verifiedState: timelineVerifiedState,
    onToggleVerified: canVerifySelection
      ? () => void setVerified(selectedForVerify, timelineVerifiedState !== "all")
      : undefined,
  };

  // Show loading state while organization context is loading
  if (!isReady || !currentOrganization || !userProfile) {
    return (
      <>
        <div className="flex min-h-[200px] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          <span className="ml-2 text-muted-foreground">{t.common.loadingOrgContext}</span>
        </div>
        <UploadQueueProgress buildingId={buildingId} />
      </>
    );
  }

  // Folder operation handlers
  const handleCreateFolder = async (data: {
    name: string;
    category: "documents" | "drawings";
    parentId?: string;
  }) => {
    try {
      // TODO: Custom folder creation was wired through templateService which queried
      // folder_view_templates/building_folder_preferences — both dropped. Wire to
      // folderService (or equivalent) when this UI is needed.
      await onRefreshDocuments();
    } catch (error) {
      console.error("Error creating folder:", error);
      toast.error(t.documents.folder.createError);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      await folderService.deleteFolder(folderId, userProfile.id.toString());
      await onRefreshDocuments();
      toast.success(t.documents.folder.deleteSuccess);
    } catch (error) {
      console.error("Error deleting folder:", error);
      toast.error(t.documents.folder.deleteError);
    }
  };

  const handleRenameFolder = async (folderId: string, newName: string) => {
    try {
      await folderService.updateFolder(folderId, { name: newName }, userProfile.id.toString());
      await onRefreshDocuments();
      toast.success(t.common.success);
    } catch (error) {
      console.error("Error renaming folder:", error);
      toast.error(t.common.error);
    }
  };

  // The store is the single source of truth for both filtering AND sorting:
  // listDocuments / goToPage apply the user's sortKey at the DB level (with
  // chained tie-breakers for nullable columns), and the documents Map preserves
  // that insertion order. We render exactly what the DB returned. (WHA-3277)
  const completedDocuments = documents;

  // Always show all folders for the building
  const filteredFolders = folders;

  // Use all filtered folders for all views
  const viewSpecificFolders = filteredFolders;

  // Enhanced tag management function
  const handleUpdateDocumentTags = async (documentId: string, folderTags: string[]) => {
    if (onUpdateDocumentTags) {
      await onUpdateDocumentTags(documentId, folderTags);
      await onRefreshDocuments(); // Refresh to show updated tags
    }
  };

  // Rendered by both the empty-state branch and the main branch.
  const uploadModal = (
    <UploadModal
      isOpen={isUploadModalOpen}
      onClose={closeUploadModal}
      buildingId={buildingId}
      scopedProject={
        scopedUploadProject ? { id: scopedUploadProject.id, name: scopedUploadProject.name } : null
      }
      // A project view starts at that project's root; building scope resolves
      // its own default (the only or default project) or asks.
      initialDestination={
        scopedUploadProject ? { projectId: scopedUploadProject.id, folderId: null } : null
      }
      pendingSelection={pendingSelection}
      onUpload={(selection, destination) => void handleUpload(selection, destination)}
    />
  );

  /**
   * The page's action row — New document (primary), Upload, and the overflow menu, all on
   * the shared control scale. They render into the page's title row via the header slot,
   * so the Documents page keeps the Issues page's layout: actions on the title row, tabs
   * beneath, tools row last.
   *
   * Defined once and rendered by BOTH the empty-state branch and the main branch. It used
   * to live only in the main branch, so a building with no documents yet showed no actions
   * at all — you could not create a document from a template in an empty building (the one
   * place a template is most useful), and you could not reach Trash to restore documents
   * after deleting the last one.
   */
  const pageActions = (
    <>
      <NewDocumentFlow
        open={isNewDocumentModalOpen}
        onOpenChange={setIsNewDocumentModalOpen}
        buildingId={buildingId}
        buildingName={buildingName}
        projectId={templateFormProjectId}
        documents={documents}
        triggerRef={newDocumentButtonRef}
        // Neither the List nor the Timeline view is scoped to a folder, so the
        // destination is asked for on Save rather than guessed at.
        saveDestination="ask"
        onDocumentFiled={() => void onRefreshDocuments()}
      />

      <PageHeaderActions>
        <PermissionGuard permission={PERMISSIONS.UPLOAD_DOCUMENTS}>
          <Button
            size="control"
            className="gap-1.5"
            onClick={handleUploadButtonClick}
            {...pageInfoTarget("files.upload")}
          >
            <Upload />
            {/* Same short label as the Folders tab. */}
            {t.projects.folder.upload}
          </Button>
        </PermissionGuard>
        {/* New document sits directly left of the overflow menu on every
            Documents tab (Folders renders the same labeled action). */}
        <PermissionGuard permission={PERMISSIONS.UPLOAD_DOCUMENTS}>
          <HeaderActionIconButton
            ref={newDocumentButtonRef}
            label={t.documents.newDocument.trigger}
            icon={<CreateGlyph icon={FileText} />}
            showLabel
            onClick={() => guardAddFiles(scopedProjectId, () => setIsNewDocumentModalOpen(true))}
            infoTarget="files.newDocument"
          />
        </PermissionGuard>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="controlIcon" {...pageInfoTarget("files.more")}>
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onManageShares}>
              <LinkIcon className="mr-2 h-4 w-4" />
              {t.documents.manage.manageShares}
            </DropdownMenuItem>
            <PermissionGuard permission={PERMISSIONS.DELETE_DATA}>
              <DropdownMenuItem onClick={onViewTrash}>
                <Trash2 className="mr-2 h-4 w-4" />
                {t.documents.trash.title}
              </DropdownMenuItem>
            </PermissionGuard>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeaderActions>
    </>
  );

  // Only show full loading state during initial load (when there are no documents yet
  // and the main content has never been shown)
  const hasExistingDocuments = documents.length > 0;
  if (isLoading && !hasExistingDocuments && !hasRenderedContentRef.current) {
    return (
      <>
        <div className="flex min-h-[200px] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        </div>
        <UploadQueueProgress
          buildingId={buildingId}
          processingCount={documentStore.processingCount}
        />
      </>
    );
  }

  // Show empty state only when there are truly no documents in the building
  // Don't show it when filters are active (that would mean no filter matches, not no documents)
  // Also don't show empty state if there are processing documents
  const hasProcessingDocuments = documentStore.processingCount > 0;
  if (
    viewMode === "list" &&
    !isLoading &&
    buildingId &&
    documents.length === 0 &&
    !hasProcessingDocuments &&
    !hasActiveFilters
  ) {
    return (
      <div className="space-y-6">
        {/* An empty building still gets the full action row. "New document" in
            particular has no other entry point, and a template is exactly what
            you want when there is nothing here yet. */}
        {pageActions}

        <DocumentsEmptyState
          onUploadClick={handleUploadButtonClick}
          onFileDrop={handleDragAndDrop}
        />

        {uploadModal}

        {/* Upload Queue Progress - must be in all return paths */}
        <UploadQueueProgress
          buildingId={buildingId}
          processingCount={documentStore.processingCount}
        />
      </div>
    );
  }

  // Mark that main content has rendered at least once
  hasRenderedContentRef.current = true;

  return (
    <div
      className={`relative min-h-[calc(100vh-12rem)] space-y-6 transition-colors ${isDragOver ? "bg-primary/5 ring-2 ring-inset ring-primary" : ""}`}
      {...dropZoneProps}
    >
      {uploadModal}

      {pageActions}

      {/* The Filters control + search live in the page-level tools row (see
          BuildingDocuments) so they're shared by every view tab. The old
          always-`hidden` GlobalDocumentSearch that used to sit here is gone —
          `hidden` still counts as a child for `space-y-6`, so it pushed the
          view's own toolbar row a whole gap further down the page. */}

      {/* Document Views */}
      <div className="space-y-4">
        {(viewMode === "list" || isTimelineView) && (
          <DocumentSearchStatusNotice
            status={
              isTimelineView ? documentStore.timelineSearchStatus : documentStore.searchStatus
            }
          />
        )}
        {viewMode === "list" ? (
          <DocumentListTable
            buildingId={buildingId}
            store={documentStore}
            documents={completedDocuments.map((doc) => ({
              ...doc,
              created_by_name: doc.created_by_name ?? null,
              state: doc.state ?? null,
              version: doc.version ?? null,
              version_group: doc.version_group ?? null,
              parent_document_id: doc.parent_document_id ?? null,
            }))}
            showStatus={false}
            isRefreshing={isLoading}
            currentPage={currentPage}
            totalCount={totalCount}
            unfilteredTotalCount={
              hasActiveFilters ? (documentStore.buildingDocumentCount ?? undefined) : undefined
            }
            hasActiveFilters={hasActiveFilters}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onShare={() => setIsShareDialogOpen(true)}
            enableSigning={signingEnabled}
            buildingName={buildingName}
            onSigned={onRefreshDocuments}
            onDocumentDeleted={() => void onRefreshDocuments()}
            onBulkDeleteComplete={() => void onRefreshDocuments()}
            onAskFranklin={onAskFranklin}
            onAddNewVersions={handleAddNewVersions}
            onRenameDocumentSubmit={onRenameDocument}
            canRenameDocument={canRenameDocument}
          />
        ) : (
          <>
            {/* Bulk action bar for timeline view.
                Single-file actions (Rename, Add new version) stay off the bar here — it acts on a selection, and these act on one
                document. The timeline exposes them per row instead, via the card's
                3-dots menu and its right-click menu. */}
            <BulkActionBar
              selectedCount={documentStore.selectedCount}
              onDelete={() => {
                // Pass the paged `documents` prop as extraRows so a selection
                // carried over from rows outside the store still resolves.
                void documentStore
                  .bulkMoveToTrash(undefined, documents ?? [])
                  .then((didTrash) => (didTrash ? onRefreshDocuments() : undefined))
                  .catch((error) => {
                    console.error("Bulk move to trash failed:", error);
                  });
              }}
              onDownload={() => {
                documentStore.bulkDownload(undefined, undefined, documents ?? []).catch((error) => {
                  console.error("Bulk download failed:", error);
                });
              }}
              onClearSelection={() => documentStore.clearSelection()}
              onShare={() => setIsShareDialogOpen(true)}
              onSignature={signingEnabled ? () => void openSignDialog() : undefined}
              onAskFranklin={() =>
                onAskFranklin?.(
                  documentStore
                    .selectedDocuments(documents)
                    .map((doc) => ({ id: String(doc.id), filename: doc.filename }))
                )
              }
              canCompareModels={timelineIfcDiff.canCompareIfcs}
              onDiffOverview={timelineIfcDiff.handleDiffOverview}
              canViewDrawing={timelineDrawings.canViewDrawing}
              onViewDrawing={timelineDrawings.handleViewSelectedDrawing}
              canCompareDrawings={timelineDrawings.canCompareDrawings}
              onCompareDrawings={timelineDrawings.handleCompareSelectedDrawings}
              onToggleVerified={timelineVerify.onToggleVerified}
              verifiedState={timelineVerify.verifiedState}
              onComments={
                commentsPanel && documentStore.selectedCount === 1
                  ? () => {
                      const [sel] = documentStore.selectedVerifiableDocuments;
                      if (!sel) return;
                      commentsPanel.open({
                        documentId: Number(sel.id),
                        buildingId: Number(buildingId),
                        filename: sel.filename,
                      });
                    }
                  : undefined
              }
            />
            {/* Owns the signature panel the timeline's signed rows open —
                the list view gets its own from DocumentListTable. Sharing
                reuses this view's dialog, driven off the store selection
                exactly like the bulk action above. */}
            <SignaturePanelProvider
              onShare={(containerId) => {
                documentStore.clearSelection();
                documentStore.selectDocument(containerId, true);
                setIsShareDialogOpen(true);
              }}
              onSign={
                signingEnabled
                  ? (container) => {
                      setSignTarget({
                        id: parseInt(container.id, 10),
                        filename: container.filename,
                        size: container.size ?? 0,
                      });
                      setIsSignDialogOpen(true);
                    }
                  : undefined
              }
            >
              <ServerDocumentTimeline
                buildingId={buildingId}
                filters={timelineFilters}
                showFilteredTotal={hasActiveFilters}
                onResolved={reportTimelineResolved}
                onRenameDocumentSubmit={onRenameDocument}
                canRenameDocument={canRenameDocument}
                // Row actions for the timeline's right-click menu — the same
                // handlers the List tab gets, so both menus offer the same set.
                onShare={() => setIsShareDialogOpen(true)}
                onAskFranklin={onAskFranklin}
                onAddNewVersions={handleAddNewVersions}
              />
            </SignaturePanelProvider>
            {/* The Timeline scrolls in the page column rather than in a
                scroller of its own, so its trailing clearance for the chat
                pill belongs here. The List tab carries its own inside
                DocumentListTable. */}
            <ChatLauncherSpacer />
          </>
        )}
      </div>

      {/* Document Upload Area - only show when actively uploading or processing */}
      {showUploadProgress && (
        <div className="rounded-lg border bg-background p-4">
          <h4 className="mb-2 font-medium">{t.documents.uploadProgressTitle}</h4>
          <PermissionGuard permission={PERMISSIONS.UPLOAD_DOCUMENTS}>
            <DocumentUpload
              buildingId={buildingId}
              onUploadComplete={() => onRefreshDocuments()}
              onDataExtracted={(data) => console.log("Data extracted:", data)}
              selectedCategory="documents"
              fileProgresses={fileProgresses}
              projectId={selectedProject?.id}
            />
          </PermissionGuard>
        </div>
      )}

      {/* Document Viewer Dialog */}
      <DocumentViewerDialog
        document={viewerDocument}
        blobUrl={null}
        open={!!viewerDocument}
        onOpenChange={(open) => {
          if (!open) setViewerDocument(null);
        }}
      />

      {/* Upload Queue Progress - fixed position component for bulk uploads */}
      <UploadQueueProgress
        buildingId={buildingId}
        processingCount={documentStore.processingCount}
      />

      {/* Upload Collision Dialog — prompts the user when incoming filenames
          clash with existing documents in the target folder. */}
      <UploadCollisionDialog
        open={!!collisionDialog}
        pairs={collisionDialog?.pairs ?? []}
        onResolve={(decisions) => collisionDialog?.resolve(decisions)}
        onCancel={() => collisionDialog?.cancel()}
      />

      {/* WHA-3695: differences overview for the timeline view (the list view
          renders its own instance inside DocumentListTable). */}
      {timelineIfcDiff.diffOverview && (
        <IfcDiffOverviewDialog
          open
          onOpenChange={(open) => {
            if (!open) timelineIfcDiff.closeDiffOverview();
          }}
          baseDocId={timelineIfcDiff.diffOverview.baseDocId}
          targetDocId={timelineIfcDiff.diffOverview.targetDocId}
          baseFilename={timelineIfcDiff.diffOverview.baseFilename}
          baseVersion={timelineIfcDiff.diffOverview.baseVersion}
          targetFilename={timelineIfcDiff.diffOverview.targetFilename}
          targetVersion={timelineIfcDiff.diffOverview.targetVersion}
          buildingId={timelineIfcDiff.diffOverview.buildingId}
        />
      )}

      {/* Share Dialog - create share link for selected documents */}
      <ShareDialog
        open={isShareDialogOpen}
        onOpenChange={(open) => {
          setIsShareDialogOpen(open);
          // Clear selection when dialog closes
          if (!open) {
            documentStore.clearSelection();
          }
        }}
        selectedDocumentIds={documentStore.selectedDocumentIds}
        buildingId={buildingId}
        orgId={currentOrganization?.id || ""}
      />

      {/* Digital signing — Dokobit Documents Gateway iframe */}
      <SignDocumentsDialog
        open={isSignDialogOpen}
        onOpenChange={(open) => {
          setIsSignDialogOpen(open);
          if (!open) {
            setSignTarget(null);
            documentStore.clearSelection();
          }
        }}
        buildingId={parseInt(buildingId, 10)}
        buildingName={buildingName}
        documents={signTarget ? [signTarget] : selectedDocumentsForSigning}
        onSigned={() => onRefreshDocuments()}
      />
    </div>
  );
});
